import { ReactNode } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

/**
 * Surface for grouped content. One border, one radius, one shadow everywhere.
 */
export const Card = styled.section<{ $interactive?: boolean; $tone?: 'default' | 'muted' }>`
  ${tw`w-full min-w-0 box-border`}
  background-color: ${({ $tone }) =>
    $tone === 'muted' ? 'var(--color-bg-secondary)' : 'var(--color-bg-elevated)'};
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: ${({ $tone }) => ($tone === 'muted' ? 'none' : 'var(--shadow-card)')};
  ${({ $interactive }) =>
    $interactive &&
    `
    cursor: pointer;
    transition: box-shadow var(--transition-normal), border-color var(--transition-normal), transform var(--transition-normal);
    &:hover { box-shadow: var(--shadow-card-hover); border-color: var(--color-border-strong); transform: translateY(-1px); }
  `}
`;

const HeaderRoot = styled.div`
  ${tw`flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between`}
  border-bottom: 1px solid var(--color-border);
`;

const HeaderText = styled.div`
  ${tw`min-w-0`}
`;

/**
 * A card's title is a section of its page, so it is an h2 under the page's h1
 * (`PageHeader`). A card that sits under another heading, such as a
 * `SectionHeader`, passes `level={3}` to `CardHeader`.
 */
export const CardTitle = styled.h2`
  ${tw`text-base font-semibold leading-6`}
  color: var(--color-text-primary);
`;

export const CardDescription = styled.p`
  ${tw`mt-0.5 text-sm`}
  color: var(--color-text-secondary);
`;

const HeaderActions = styled.div`
  ${tw`flex flex-wrap items-center gap-2 shrink-0`}
`;

export type CardHeadingLevel = 2 | 3 | 4;

export function CardHeader({
  title,
  description,
  actions,
  className,
  level = 2,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Heading level of the title. 2 under the page title; 3 under an h2 such as `SectionHeader`. */
  level?: CardHeadingLevel;
}) {
  return (
    <HeaderRoot className={className}>
      <HeaderText>
        <CardTitle as={`h${level}` as 'h2' | 'h3' | 'h4'}>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </HeaderText>
      {actions && <HeaderActions>{actions}</HeaderActions>}
    </HeaderRoot>
  );
}

export const CardBody = styled.div<{ $padding?: 'none' | 'sm' | 'md' }>`
  ${({ $padding = 'md' }) =>
    $padding === 'none' ? tw`p-0` : $padding === 'sm' ? tw`p-4` : tw`p-5`}
`;

export const CardFooter = styled.div`
  ${tw`flex flex-wrap items-center justify-end gap-2 px-5 py-3`}
  border-top: 1px solid var(--color-border);
  background-color: var(--color-bg-secondary);
  border-bottom-left-radius: var(--radius-lg);
  border-bottom-right-radius: var(--radius-lg);
`;

/** Row inside a card list: divider between rows, hover tint. */
export const CardRow = styled.div<{ $interactive?: boolean }>`
  ${tw`flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between`}
  & + & {
    border-top: 1px solid var(--color-border);
  }
  ${({ $interactive }) =>
    $interactive &&
    `
    cursor: pointer;
    transition: background-color var(--transition-normal);
    &:hover { background-color: var(--color-bg-secondary); }
  `}
`;
